import {
  ArrowRight,
  Document,
  Graph,
  MessageQuestion,
  NoteRemove,
  Refresh,
  Shield,
} from '../icons';
import { useTranslation } from 'react-i18next';
import { PRIVACY_POLICY_URLS, supportEmail, supportSubject } from '../../constants/constants';
import { useLanguage } from '../../context/LanguageContext';
import { useEffect, useRef, useState } from 'react';
import ProfileModal from './profileModal';
import OpenSourceList from './openSourceList';
import { getAddInInformation } from '../../constants/utilityFunctions';
import {
  Paragraph,
  SettingButton,
  SettingButtonList,
  SettingRow,
  Spin,
  Switch,
  Text,
} from '../Ant';
import { Flex } from 'antd';
import { useAnalytics } from '../../hooks/useAnalytics';
import ReleaseNotes from './releaseNotes';
import AppUpdateChecker from '../appUpdate/appUpdateChecker';
import { ApplicationSettingsWrapper } from '../../assets/styles/components/settingsPopover.styled';
import { usePage } from '../../context/PageContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';

const ProfileAbout = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReleaseNotesModalOpen, setIsReleaseNotesModalOpen] = useState(false);
  const [showUpdateChecker, setShowUpdateChecker] = useState<boolean>(false);
  const [isLoadingUpdateChecker, setIsLoadingUpdateChecker] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { language } = useLanguage();
  const { hasAnalyticsPermission, setAnalyticsPermission } = useAnalytics();
  const { setTitle } = usePage();
  const { goBack } = useNavigationStack();

  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        title={t('common:profile.about')}
        showMenu
        showSettings
        showHomeWithBackButton={false}
      />,
    );
  }, []);
  const handleBack = () => {
    goBack();
  };

  const handleProfileLegal = () => {
    window.open(
      PRIVACY_POLICY_URLS[language as keyof typeof PRIVACY_POLICY_URLS] ?? PRIVACY_POLICY_URLS.en,
      '_blank',
    );
  };
  const handleNeedSupport = () => {
    if (Office?.context?.mailbox) {
      const emailBody = getAddInInformation(t);
      Office.context.mailbox.displayNewMessageForm({
        toRecipients: [supportEmail],
        subject: supportSubject,
        htmlBody: emailBody,
      });
    }
  };
  const handleAnalyticsTracking = (value: boolean) => {
    setAnalyticsPermission(value);
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <Flex vertical gap={20}>
      {isLoadingUpdateChecker && <Spin fullscreen />}
      <Flex vertical gap={10}>
        <Paragraph $fontsize="12px" $color="subtitle" $weight={500} $lineheight="normal">
          {t('common:profile.legal')}
        </Paragraph>
        <SettingButtonList>
          <SettingButton onClick={handleProfileLegal} className="profile-about-btn">
            <span className="start-icon">
              <Shield />
            </span>
            <div className="w-full d-flex items-center">
              <Flex vertical className="w-full">
                <Text
                  $fontsize="13px"
                  $weight={500}
                  $lineheight="20px"
                  $color="title"
                  className="ta-left"
                  data-testid="privacy-policy-link"
                >
                  {t('common:profile.privacyPolicy')}
                </Text>
              </Flex>
              <span className="end-icon">
                <ArrowRight className="fs-20" />
              </span>
            </div>
          </SettingButton>
          <SettingButton
            data-testid="open-source-button"
            onClick={openModal}
            className="profile-about-btn"
          >
            <span className="start-icon">
              <Document />
            </span>
            <div className="w-full d-flex items-center">
              <Flex vertical className="w-full">
                <Text
                  $fontsize="13px"
                  $weight={500}
                  $lineheight="20px"
                  $color="title"
                  className="ta-left"
                  data-testid="open-source-text"
                >
                  {t('common:profile.useOfOpenSource')}
                </Text>
              </Flex>
              <span className="end-icon">
                <ArrowRight className="fs-20" />
              </span>
            </div>
          </SettingButton>
          <SettingRow className="profile-about-btn">
            <span className="start-icon">
              <Graph />
            </span>
            <div className="w-full d-flex items-center">
              <Flex vertical className="w-full">
                <Text
                  $fontsize="13px"
                  $weight={500}
                  $lineheight="20px"
                  $color="title"
                  className="ta-left"
                  data-testid="analytics-text"
                >
                  {t('common:analyticsDataTracking')}
                </Text>
              </Flex>
              <span className="end-icon">
                <Switch
                  data-testid="analytics-switch-id"
                  aria-label={t('common:analyticsDataTracking')}
                  value={!!hasAnalyticsPermission}
                  onChange={handleAnalyticsTracking}
                />
              </span>
            </div>
          </SettingRow>
        </SettingButtonList>
        {isModalOpen && (
          <ProfileModal
            data-testid="open-source-modal"
            title={t('common:profile.useOfOpenSource')}
            onClose={closeModal}
            isOpen={isModalOpen}
            targetRef={modalRef}
            resizable
          >
            <OpenSourceList />
          </ProfileModal>
        )}
      </Flex>
      <Flex vertical gap={10}>
        <Paragraph $fontsize="12px" $color="subtitle" $weight={500} $lineheight="normal">
          {t('common:profile.updates')}
        </Paragraph>
        <SettingButtonList>
          <SettingButton className="profile-about-btn" onClick={() => setShowUpdateChecker(true)}>
            <span className="start-icon">
              <Refresh />
            </span>
            <div className="w-full d-flex items-center">
              <Flex vertical className="w-full">
                <Text
                  data-testid="check-for-updates-text"
                  $fontsize="13px"
                  $weight={500}
                  $lineheight="20px"
                  $color="title"
                  className="ta-left"
                >
                  {t('common:profile.checkForUpdates')}
                </Text>
              </Flex>
              <span className="end-icon">
                <ArrowRight className="fs-20" />
              </span>
            </div>
          </SettingButton>
          <SettingButton
            className="profile-about-btn"
            onClick={() => setIsReleaseNotesModalOpen(true)}
          >
            <span className="start-icon">
              <NoteRemove />
            </span>
            <div className="w-full d-flex items-center">
              <Flex vertical className="w-full">
                <Text
                  data-testid="need-support-text"
                  $fontsize="13px"
                  $weight={500}
                  $lineheight="20px"
                  $color="title"
                  className="ta-left"
                >
                  {t('common:profile.releaseNotes')}
                </Text>
              </Flex>
              <span className="end-icon">
                <ArrowRight className="fs-20" />
              </span>
            </div>
          </SettingButton>
        </SettingButtonList>
        {isReleaseNotesModalOpen && (
          <ProfileModal
            data-testid="release-notes-modal"
            title={t('common:profile.releaseNotes')}
            onClose={() => setIsReleaseNotesModalOpen(false)}
            isOpen={isReleaseNotesModalOpen}
            resizable
          >
            <ReleaseNotes />
          </ProfileModal>
        )}
      </Flex>
      <Flex vertical gap={10}>
        <Paragraph $fontsize="12px" $color="subtitle" $weight={500} $lineheight="normal">
          {t('common:profile.support')}
        </Paragraph>
        <SettingButtonList>
          <SettingButton
            data-testid="support-button"
            onClick={handleNeedSupport}
            className="profile-about-btn"
          >
            <span className="start-icon">
              <MessageQuestion />
            </span>
            <div className="w-full d-flex items-center">
              <Flex vertical className="w-full">
                <Text
                  data-testid="need-support-text"
                  $fontsize="13px"
                  $weight={500}
                  $lineheight="20px"
                  $color="title"
                  className="ta-left"
                >
                  {t('common:profile.needSupport')}
                </Text>
              </Flex>
              <span className="end-icon">
                <ArrowRight className="fs-20" />
              </span>
            </div>
          </SettingButton>
        </SettingButtonList>
      </Flex>
      <Flex vertical gap={10}>
        <Paragraph $fontsize="12px" $color="subtitle" $weight={500} $lineheight="normal">
          {t('common:profile.about')}
        </Paragraph>
        <ApplicationSettingsWrapper vertical gap={10} className="p-gutter">
          <Flex align="center" justify="space-between">
            <Paragraph $fontsize="12px" $color="title" $weight={400} $lineheight="20px">
              {t('common:profile.addInVersion')}
            </Paragraph>
            <Paragraph
              data-testid="add-in-version"
              $fontsize="12px"
              $color="title"
              $weight={600}
              $lineheight="18px"
            >
              {__ADD_IN_VERSION__}
            </Paragraph>
          </Flex>
          <Flex align="center" justify="space-between">
            <Paragraph $fontsize="12px" $color="title" $weight={400} $lineheight="20px">
              {t('common:profile.manifestVersion')}
            </Paragraph>
            <Paragraph
              data-testid="manifest-version"
              $fontsize="12px"
              $color="title"
              $weight={600}
              $lineheight="18px"
            >
              {__MANIFEST_VERSION__}
            </Paragraph>
          </Flex>
        </ApplicationSettingsWrapper>
      </Flex>
      {showUpdateChecker && (
        <AppUpdateChecker
          fromProfile={true}
          onCloseChecker={() => setShowUpdateChecker(false)}
          onLoadingChange={(loading) => setIsLoadingUpdateChecker(loading)}
        />
      )}
    </Flex>
  );
};

export default ProfileAbout;
